import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, Gavel } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchStrikes, type AdminStrike } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/** Severity 1..5 → rang (dizayn §2 risk-shkalasi). */
function sevClass(n: number): string {
  if (n >= 4) return 'text-danger'
  if (n === 3) return 'text-warning'
  return 'text-success'
}

/**
 * A8.03 — So'nggi strayklar (progressiv-jazo). GET /admin/strikes (backend
 * `0252c93`). Strayklar report-qaroridan yoki qo'lda beriladi (A8.05).
 */
export function StrikesQueue() {
  const t = useT()

  const columns = useMemo<AdminColumn<AdminStrike>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'user_id',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'severity',
        header: t.strikes.severity,
        type: 'number',
        width: 100,
        align: 'right',
        render: (r) => <span className={cn('font-mono tabular font-medium', sevClass(r.severity))}>{r.severity}</span>,
      },
      { key: 'reason', header: t.mod.reason, type: 'longtext', minWidth: 200 },
      { key: 'issued_by', header: t.strikes.issuedBy, type: 'text', width: 140 },
      {
        key: 'active',
        header: t.col.status,
        type: 'enum',
        width: 120,
        accessor: (r) => (r.active ? 'active' : 'expired'),
        options: [
          { value: 'active', label: t.strikes.active, status: 'active' },
          { value: 'expired', label: t.strikes.expired, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchStrikes, [])

  return (
    <>
      <PageHeader
        title={t.strikes.queue}
        screenId="A8.03"
        description={t.strikes.queueNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/strikes/stats"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BarChart3 size={13} />
              {t.strikes.statsTitle}
            </Link>
            <Link
              to="/appeals"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              {t.appeals.queue}
            </Link>
          </div>
        }
      />
      <DataTable<AdminStrike>
        screenId="A8.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Gavel size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
